import { Component, OnInit, inject } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { catchError, map, Observable } from 'rxjs';
import { Tag } from 'src/app/entities/tag/tag';
import { StoreService } from 'src/app/services/store-service/store.service';
import { TagRepository } from 'src/app/services/remote-data-service/repositories/tag-repository/tag.repository';
import { Roles, UserRepository } from 'src/app/services/remote-data-service/repositories/user-repository/user.repository';
import { MatDialog } from '@angular/material/dialog';
import { OperationType } from 'src/app/models/operation-types';
import { MatButtonModule } from '@angular/material/button';
import { SnackbarService } from 'src/app/services/snackbar-service/snackbar.service';
import { TagsMutationsComponent } from 'src/app/components/atoms/mutations/tags-mutations/tags-mutations.component';
import { EntityItemsAnnihilatorComponent } from 'src/app/components/atoms/mutations/entity-items-annihilator/entity-items-annihilator.component';
import { FiltersService } from 'src/app/services/filters-service/filters.service';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';

interface OrderedTags {
  letter: string;
  tags: Tag[]
}

@Component({
  selector: 'app-tag-manager-page',
  templateUrl: './tag-manager-page.component.html',
  styleUrls: ['./tag-manager-page.component.scss'],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatButtonModule,
    MatIconModule,
    MatFormFieldModule,
    MatInputModule
  ]
})
export class TagManagerPageComponent implements OnInit {
  tags: Tag[] = [];
  tags$: Observable<Tag[]>;
  groupedTags: OrderedTags[] = [];

  public form = new FormGroup({
    name: new FormControl('', Validators.required),
  });

  isManager = false;

  readonly dialog = inject(MatDialog);

  constructor(
    protected store: StoreService,
    protected tagsRep: TagRepository,
    protected userRep: UserRepository,
    protected snackbar: SnackbarService,
    protected filters: FiltersService
  ) {
    this.tags$ = store.tags;

    this.tags$.pipe(
      catchError((err) => {
        console.error(err);
        return []
      }),
      map((tags) => {
        this.tags = tags;
        return tags;
      }),
      map((tags) => {
        this.orderTags(tags)
      })
    ).subscribe();
  }

  ngOnInit(): void {
    this.filters.setTagsFilters({
      limit: 99999
    });

    if (this.filters.hasFormValues('tagsFilters')) {
      this.form.patchValue(
        this.filters.getFormValues<{ name: string }>('tagsFilters') || {}
      );
    }

    this.form.valueChanges.subscribe((changes) => {
      this.filters.saveFormValues('tagsFilters', changes);
      this.filters.setTagsFilters({
        name: changes.name || undefined,
      });
    });

    this.userRep.getCurrentUser().subscribe((user) => {
      this.isManager = user.roles.includes(Roles.chronos_manager);
    });
  }

  orderTags(tagsRes: Tag[]) {
    const tags = tagsRes.sort((a, b) => a.label.localeCompare(b.label));
    const grouped = tags.reduce((initial, tag) => {
      const letter = tag.label.charAt(0).toUpperCase();
      const group = initial.find(g => g.letter === letter);
      if (group) group.tags.push(tag)
      else initial.push({ letter, tags: [tag] });
      return initial;
    }, [] as OrderedTags[]);
    this.groupedTags = grouped.sort((a, b) => a.letter.localeCompare(b.letter));
  }

  openCreateTagDialog(enterAnimationDuration: string, exitAnimationDuration: string): void {
    this.dialog.open(TagsMutationsComponent, {
      width: 'auto',
      height: 'auto',
      maxWidth: '80dvw',
      maxHeight: '80dvh',
      enterAnimationDuration,
      exitAnimationDuration,
      data: { operation: OperationType.CREATE }
    });
  }

  openUpdateTagDialog(tagId: string, enterAnimationDuration: string, exitAnimationDuration: string): void {
    this.dialog.open(TagsMutationsComponent, {
      width: 'auto',
      height: 'auto',
      maxWidth: '80dvw',
      maxHeight: '80dvh',
      enterAnimationDuration,
      exitAnimationDuration,
      data: { operation: OperationType.UPDATE, id: tagId }
    });
  }

  deleteTag(tag: Tag) {
    this.tagsRep.removeTag(tag).pipe(
      catchError(() => {
        return [];
      })
    ).subscribe(() => {
      const tags = this.tags.filter(e => e !== tag);
      this.tags = tags;
      this.snackbar.showSuccess('Tag deleted');
      this.orderTags(tags);
    });
  }

  openItemAnnihilatorDialog(enterAnimationDuration: string, exitAnimationDuration: string): void {
    this.dialog.open(EntityItemsAnnihilatorComponent<Tag>, {
      width: 'auto',
      height: 'auto',
      maxWidth: '80dvw',
      maxHeight: '80dvh',
      enterAnimationDuration,
      exitAnimationDuration,
      data: { entities: this.tags }
    });
  }
}
